import React, {useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {publicApi} from '../api';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

export default function SigningPage() {
  const {token} = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [signatures, setSignatures] = useState({});
  const [consent, setConsent] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [signMode, setSignMode] = useState('draw'); // 'draw' | 'type'
  const [typedText, setTypedText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [pageImages, setPageImages] = useState([]);
  const canvasRef = useRef();
  const drawing = useRef(false);

  useEffect(() => {
    publicApi(`/esign/sign/${token}`).then(r => {
      if (r.ok) r.json().then(data => {
        setSession(data);
        // Auto-fill known fields
        const auto = {};
        const nameParts = data.signerName ? data.signerName.split(' ') : [];
        const today = new Date();
        data.fields?.forEach(f => {
          if (f.fieldType === 'NAME') auto[f.id] = {fieldId: f.id, signatureType: 'TYPED', signatureData: data.signerName};
          else if (f.fieldType === 'FIRST_NAME') auto[f.id] = {fieldId: f.id, signatureType: 'TYPED', signatureData: nameParts[0] || ''};
          else if (f.fieldType === 'LAST_NAME') auto[f.id] = {fieldId: f.id, signatureType: 'TYPED', signatureData: nameParts.slice(1).join(' ') || ''};
          else if (f.fieldType === 'EMAIL') auto[f.id] = {fieldId: f.id, signatureType: 'TYPED', signatureData: data.signerEmail};
          else if (f.fieldType === 'DATE') auto[f.id] = {fieldId: f.id, signatureType: 'TYPED', signatureData: today.toLocaleDateString()};
          else if (f.fieldType === 'DATE_DAY') auto[f.id] = {fieldId: f.id, signatureType: 'TYPED', signatureData: today.getDate().toString()};
          else if (f.fieldType === 'DATE_MONTH') auto[f.id] = {fieldId: f.id, signatureType: 'TYPED', signatureData: today.toLocaleString('default', {month: 'long'})};
          else if (f.fieldType === 'DATE_YEAR_4') auto[f.id] = {fieldId: f.id, signatureType: 'TYPED', signatureData: today.getFullYear().toString()};
          else if (f.fieldType === 'DATE_YEAR_2') auto[f.id] = {fieldId: f.id, signatureType: 'TYPED', signatureData: today.getFullYear().toString().slice(-2)};
        });
        if (Object.keys(auto).length) setSignatures(prev => ({...prev, ...auto}));
      });
      else setError('This signing link is invalid or has expired.');
    });
  }, [token]);

  // Render PDF pages as images
  useEffect(() => {
    if (!session?.documentUrl) return;
    let cancelled = false;
    pdfjsLib.getDocument(session.documentUrl).promise.then(pdf => {
      if (cancelled) return;
      const promises = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        promises.push(pdf.getPage(i).then(page => {
          const viewport = page.getViewport({scale: 1.5});
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          return page.render({canvasContext: canvas.getContext('2d'), viewport}).promise.then(() => ({
            src: canvas.toDataURL(),
            width: viewport.width,
            height: viewport.height,
            pageNum: i,
          }));
        }));
      }
      return Promise.all(promises);
    }).then(images => { if (!cancelled && images) setPageImages(images); }).catch(() => {});
    return () => { cancelled = true; };
  }, [session?.documentUrl]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => { drawing.current = false; };

  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const saveSignature = () => {
    if (!activeField) return;
    let data;
    let type;
    if (isTextInputField(activeField.fieldType)) {
      if (!typedText.trim()) return;
      data = typedText.trim();
      type = 'TYPED';
    } else if (signMode === 'draw') {
      data = canvasRef.current.toDataURL('image/png');
      type = 'DRAWN';
    } else {
      if (!typedText.trim()) return;
      data = typedText.trim();
      type = 'TYPED';
    }
    setSignatures(prev => ({...prev, [activeField.id]: { fieldId: activeField.id, signatureType: type, signatureData: data }}));
    setActiveField(null);
    setTypedText('');
  };

  const isAutoField = (type) => ['NAME','EMAIL','DATE','FIRST_NAME','LAST_NAME','DATE_DAY','DATE_MONTH','DATE_YEAR_4','DATE_YEAR_2'].includes(type);
  const isTextInputField = (type) => ['FREE_TEXT','STREET_ADDRESS_1','STREET_ADDRESS_2','CITY','STATE','ZIP_CODE','COUNTRY','COUNTRY_ABBR'].includes(type);

  const handleSubmit = async () => {
    const sigs = Object.values(signatures);
    if (sigs.length !== session.fields.length) return;
    setSubmitting(true);
    const res = await publicApi(`/esign/sign/${token}/complete`, {
      method: 'POST',
      body: JSON.stringify({ signatures: sigs }),
    });
    if (res.ok) setDone(true);
    setSubmitting(false);
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Sign</h1>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (!session) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-gray-500">Loading...</div>
    </div>
  );

  if (session.status === 'SIGNED' || done) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center bg-white rounded-lg shadow-lg p-8 max-w-md">
        <div className="text-4xl mb-4">✓</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Document Signed</h1>
        <p className="text-gray-500 mb-6">Thank you, {session.signerName}. Your signature has been recorded.</p>
        {session.documentUrl && (
          <a href={`/sign/${token}/document`}
            className="inline-block px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium text-sm">
            View Document
          </a>
        )}
      </div>
    </div>
  );

  const allFieldsSigned = session.fields.every(f => signatures[f.id]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex justify-between items-center">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-emerald-600"><img src="/images/mydocumentsign-logo.png" alt="MyDocumentSign" className="h-12 sm:h-16" style={{maxHeight: '5rem'}} /></h1>
            <p className="text-xs sm:text-sm text-gray-500 truncate">{session.documentTitle}</p>
          </div>
          <div className="text-right flex-shrink-0 ml-4">
            <p className="text-xs sm:text-sm text-gray-900 font-medium">{session.signerName}</p>
            <p className="text-xs text-gray-500 hidden sm:block">{session.signerEmail}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Consent */}
        {!consent && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Electronic Signature Consent</h2>
            <p className="text-gray-600 text-sm mb-5">Before proceeding, please confirm your agreement below.</p>
            <div className="flex items-start gap-3 p-4 border rounded-lg bg-gray-50">
              <input type="checkbox" id="consent" className="mt-0.5 w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                onChange={e => setConsent(e.target.checked)} />
              <label htmlFor="consent" className="text-sm sm:text-base text-gray-900">
                Do you agree to use electronic records and electronic signatures for this transaction?
              </label>
            </div>
          </div>
        )}

        {consent && <>
        {/* Document with interactive fields */}
        {pageImages.length > 0 && (
          <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Click on the highlighted fields to sign</h2>
            </div>
            <div className="space-y-1 bg-gray-100 p-2">
              {pageImages.map(({src, width, height, pageNum}) => {
                const pageFields = session.fields.filter(f => f.pageNumber === pageNum);
                return (
                  <div key={pageNum} className="relative">
                    <img src={src} alt={`Page ${pageNum}`} className="w-full rounded shadow-sm" />
                    {pageFields.map(f => {
                      const scale = 1.5;
                      const left = `${(f.xPosition * scale / width) * 100}%`;
                      const top = `${(f.yPosition * scale / height) * 100}%`;
                      const w = `${(f.width * scale / width) * 100}%`;
                      const h = `${(f.height * scale / height) * 100}%`;
                      const signed = signatures[f.id];
                      const isClickable = !signed && !isAutoField(f.fieldType) && !isTextInputField(f.fieldType);
                      return (
                        <div key={f.id} style={{position: 'absolute', left, top, width: w, height: h}}
                          onClick={() => {
                            if (signed && !isAutoField(f.fieldType)) { setSignatures(prev => { const n = {...prev}; delete n[f.id]; return n; }); }
                            else if (!signed && (f.fieldType === 'SIGNATURE' || f.fieldType === 'INITIALS')) { setActiveField(f); setSignMode('draw'); }
                            else if (!signed && isTextInputField(f.fieldType)) { setActiveField(f); }
                          }}
                          className={`rounded flex overflow-hidden ${
                            signed && signed.signatureType === 'DRAWN' ? 'border-2 border-green-500 bg-green-50/90 items-end justify-center'
                              : signed ? 'items-end justify-start'
                              : isClickable ? 'border-2 border-emerald-500 bg-emerald-50/90 text-emerald-700 cursor-pointer hover:bg-emerald-100/90 animate-pulse items-end justify-center'
                              : isTextInputField(f.fieldType) && !signed ? 'border-2 border-amber-500 bg-amber-50/90 text-amber-700 cursor-pointer hover:bg-amber-100/90 animate-pulse items-end justify-center'
                              : 'border-2 border-green-500 bg-green-50/90 text-green-700 items-end justify-center'
                          }`}>
                          {signed ? (
                            signed.signatureType === 'DRAWN' ? (
                              <img src={signed.signatureData} alt="signature" className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-sm font-medium truncate px-1 text-gray-900">{signed.signatureData}</span>
                            )
                          ) : (
                            <span className="text-[10px] sm:text-xs font-medium text-center leading-tight">
                              {{'SIGNATURE':'✍ Sign here','INITIALS':'Initial','DATE':'📅','NAME':'👤','EMAIL':'✉','STREET_ADDRESS_1':'📍 Addr 1','STREET_ADDRESS_2':'Addr 2','CITY':'City','STATE':'State','ZIP_CODE':'Zip','COUNTRY':'Country','COUNTRY_ABBR':'Ctry','FIRST_NAME':'👤 First','LAST_NAME':'👤 Last','DATE_DAY':'Day','DATE_MONTH':'Month','DATE_YEAR_4':'Year','DATE_YEAR_2':'Yr','FREE_TEXT': f.label || '✏ Fill in'}[f.fieldType] || f.fieldType}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button onClick={handleSubmit} disabled={!allFieldsSigned || submitting}
          className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50">
          {submitting ? 'Submitting...' : 'Complete Signing'}
        </button>
        </>}
      </main>

      {activeField && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setActiveField(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {activeField.fieldType === 'SIGNATURE' ? 'Sign Here' : activeField.fieldType === 'INITIALS' ? 'Initial Here' : isTextInputField(activeField.fieldType) ? ({'STREET_ADDRESS_1':'Street Address 1','STREET_ADDRESS_2':'Street Address 2','CITY':'City','STATE':'State','ZIP_CODE':'Zip Code','COUNTRY':'Country','COUNTRY_ABBR':'Country Abbreviation','FREE_TEXT': activeField.label || 'Enter Text'}[activeField.fieldType] || 'Enter Text') : 'Enter Date'}
            </h3>

            {isTextInputField(activeField.fieldType) ? (
              <input type="text" autoFocus value={typedText} onChange={e => setTypedText(e.target.value)}
                placeholder={{'STREET_ADDRESS_1':'123 Main St','STREET_ADDRESS_2':'Apt 4B','CITY':'City','STATE':'State','ZIP_CODE':'12345','COUNTRY':'United States','COUNTRY_ABBR':'US','FREE_TEXT': activeField.label || 'Enter text'}[activeField.fieldType] || 'Enter text'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 outline-none" />
            ) : (<>
              <div className="flex gap-2 mb-4">
                <button onClick={() => setSignMode('draw')}
                  className={`px-3 py-1.5 rounded text-sm ${signMode === 'draw' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Draw</button>
                <button onClick={() => setSignMode('type')}
                  className={`px-3 py-1.5 rounded text-sm ${signMode === 'type' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Type</button>
              </div>

              {signMode === 'draw' ? (
                <div>
                  <canvas ref={canvasRef} width={450} height={150}
                    className="border border-gray-300 rounded-lg cursor-crosshair w-full"
                    onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                    onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
                  <button onClick={clearCanvas} className="text-sm text-gray-500 hover:text-gray-700 mt-2">Clear</button>
                </div>
              ) : (
                <input type="text" autoFocus value={typedText} onChange={e => setTypedText(e.target.value)}
                  placeholder={activeField.fieldType === 'DATE' ? new Date().toLocaleDateString() : 'Type your signature'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-2xl font-script focus:ring-2 focus:ring-emerald-500 outline-none" />
              )}
            </>)}

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setActiveField(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={saveSignature} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
